import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Calculator } from "lucide-react";
import {
  getChargeSchedule,
  previewCharges,
  type ChargePreview,
  type TradeLeg,
} from "../api/charges";
import Button from "./Button";
import { apiErrorMessage } from "../lib/api";

/** Check the built-in rate card against a real contract note.
 *
 *  This exists because published rates go stale and there is no way to know
 *  from inside the app. On 2026-08-19 Upstox's own charges page still showed
 *  options STT at 0.1% — it had been raised to 0.15% five months earlier. A
 *  backtest built on the stale figure understates every option exit by 50% of
 *  its largest single cost, silently, and looks entirely plausible.
 *
 *  So the check is: enter the trades from a contract note, compare line by
 *  line, and if anything differs the shipped rate has changed.
 */
const BLANK: TradeLeg = { side: "BUY", quantity: "", price: "" };

const LABELS: Record<string, string> = {
  brokerage: "Brokerage",
  stt: "STT / CTT",
  exchange_txn: "Exchange turnover fee",
  sebi: "SEBI turnover fee",
  stamp_duty: "Stamp duty",
  gst: "GST",
  total: "Total",
};

export default function ChargesVerifier({ broker }: { broker: string }) {
  const [open, setOpen] = useState(false);
  const [exchange, setExchange] = useState(broker === "delta" ? "DELTA" : "NFO");
  const [instrumentType, setInstrumentType] = useState(
    broker === "delta" ? "PERP" : "PE",
  );
  const [legs, setLegs] = useState<TradeLeg[]>([{ ...BLANK }]);
  const [result, setResult] = useState<ChargePreview | null>(null);

  const schedule = useQuery({
    queryKey: ["charge-schedule"],
    queryFn: getChargeSchedule,
    staleTime: 60 * 60 * 1000,
    enabled: open,
  });

  const preview = useMutation({
    mutationFn: () =>
      previewCharges({
        broker,
        exchange,
        instrument_type: instrumentType,
        legs: legs.filter((l) => l.quantity && l.price),
      }),
    onSuccess: setResult,
  });

  const rates = schedule.data?.brokers?.[broker];
  const canRun = legs.some((l) => l.quantity && l.price);

  if (!open) {
    return (
      <Button
        variant="secondary"
        size="sm"
        type="button"
        onClick={() => setOpen(true)}
        iconLeft={<Calculator size={14} />}
        title="Compare the charges we model against a real contract note"
      >
        Check brokerage &amp; charges
      </Button>
    );
  }

  return (
    <div className="border border-border rounded-lg p-4 space-y-4 bg-surface-2 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-medium text-fg">Brokerage &amp; charges</div>
          <p className="text-xs text-muted mt-1 max-w-prose">
            Enter the trades from a recent contract note and compare each line.
            Rates change by government notification, and broker pages are not
            always current — if a line differs from your bill, correct the rate
            below.
          </p>
        </div>
        <Button variant="ghost" size="sm" type="button" onClick={() => setOpen(false)}>
          Close
        </Button>
      </div>

      {schedule.data && (
        <div className="text-xs text-muted">
          Rates last verified against a real contract note on{" "}
          <span className="font-mono text-fg">{schedule.data.rates_verified_on}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-fg-subtle mb-1">Exchange</label>
          <input
            value={exchange}
            onChange={(e) => setExchange(e.target.value.toUpperCase())}
            className="w-full border border-border rounded px-2 py-1.5 font-mono"
          />
        </div>
        <div>
          <label className="block text-xs text-fg-subtle mb-1">Instrument type</label>
          <input
            value={instrumentType}
            onChange={(e) => setInstrumentType(e.target.value.toUpperCase())}
            className="w-full border border-border rounded px-2 py-1.5 font-mono"
            placeholder="PE / CE / FUT / EQ / PERP"
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium text-fg-subtle">
          Trades (one row per executed order — brokerage is billed per order)
        </div>
        {legs.map((leg, i) => (
          <div key={i} className="flex gap-2">
            <select
              value={leg.side}
              onChange={(e) => {
                const next = [...legs];
                next[i] = { ...leg, side: e.target.value as "BUY" | "SELL" };
                setLegs(next);
              }}
              className="border border-border rounded px-2 py-1.5"
            >
              <option value="BUY">BUY</option>
              <option value="SELL">SELL</option>
            </select>
            <input
              value={leg.quantity}
              onChange={(e) => {
                const next = [...legs];
                next[i] = { ...leg, quantity: e.target.value };
                setLegs(next);
              }}
              placeholder="Qty"
              className="w-24 border border-border rounded px-2 py-1.5 font-mono"
            />
            <input
              value={leg.price}
              onChange={(e) => {
                const next = [...legs];
                next[i] = { ...leg, price: e.target.value };
                setLegs(next);
              }}
              placeholder="Avg price"
              className="flex-1 border border-border rounded px-2 py-1.5 font-mono"
            />
            {legs.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => setLegs(legs.filter((_, j) => j !== i))}
              >
                ×
              </Button>
            )}
          </div>
        ))}
        <Button
          variant="ghost"
          size="sm"
          type="button"
          onClick={() => setLegs([...legs, { ...BLANK }])}
        >
          + Add trade
        </Button>
      </div>

      <div className="flex gap-2">
        <Button
          size="sm"
          type="button"
          disabled={!canRun || preview.isPending}
          onClick={() => preview.mutate()}
        >
          {preview.isPending ? "Calculating…" : "Calculate charges"}
        </Button>
      </div>

      {preview.isError && (
        <div className="text-xs text-down-700 dark:text-down-400">
          {apiErrorMessage(preview.error)}
        </div>
      )}

      {result && (
        <div className="space-y-3">
          <div className="border border-border rounded overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <tbody>
                  {["brokerage", "stt", "exchange_txn", "sebi", "stamp_duty", "gst"].map(
                    (k) => (
                      <tr key={k} className="border-b border-border/60">
                        <td className="px-3 py-1.5 text-fg-subtle">{LABELS[k]}</td>
                        <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                          ₹{result.totals[k]}
                        </td>
                      </tr>
                    ),
                  )}
                  <tr className="bg-surface-2 font-semibold">
                    <td className="px-3 py-2">{LABELS.total}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">
                      ₹{result.totals.total}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs text-muted">{result.note}</p>
        </div>
      )}

      {rates && (
        <details className="text-xs">
          <summary className="cursor-pointer text-fg-subtle">
            Rate card in use for {broker}
          </summary>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full font-mono text-[11px]">
              <thead>
                <tr className="text-muted text-left">
                  <th className="py-1 pr-3">Segment</th>
                  <th className="py-1 pr-3">Brokerage</th>
                  <th className="py-1 pr-3">STT</th>
                  <th className="py-1 pr-3">Exchange</th>
                  <th className="py-1">Stamp</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(rates).map(([seg, r]) => (
                  <tr key={seg} className="border-t border-border/60">
                    <td className="py-1 pr-3">{seg}</td>
                    <td className="py-1 pr-3">
                      {Number(r.brokerage_flat) > 0
                        ? `₹${r.brokerage_flat}/order`
                        : `${(Number(r.brokerage_pct) * 100).toFixed(3)}%${
                            r.brokerage_cap ? ` cap ₹${r.brokerage_cap}` : ""
                          }`}
                    </td>
                    <td className="py-1 pr-3">
                      {(Number(r.stt_pct) * 100).toFixed(4)}%
                    </td>
                    <td className="py-1 pr-3">
                      {(Number(r.exchange_txn_pct) * 100).toFixed(5)}%
                    </td>
                    <td className="py-1">{(Number(r.stamp_pct) * 100).toFixed(4)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}
